import { ProFormColumnsType } from '@ant-design/pro-components';
import { Card } from 'antd';
import { FormattedMessage } from 'react-intl';
import { IncomeType } from '../../../../api/btlApplicationForm/types';
import { DeleteIcon } from '../../../../components/common/Icons';
import { BaseFormSchemaProps } from '../../types';
import { createCurrencyFormItem } from './createCurrencyFormItem';
import {
  companyDirectorFormSchema,
  contractorFormSchema,
  partnershipFormSchema,
  payeEmployeeFormSchema,
  soleTraderFormSchema,
} from './MemberIncome';

export type HouseholdMemberSchemaProps = BaseFormSchemaProps & {
  isEmploymentIncomeRequired?: boolean;
  variant?: 'btl' | 'oo';
};

export const householdMemberFormSchema = (props: HouseholdMemberSchemaProps): ProFormColumnsType[] => [
  {
    dataIndex: ['incomes'],
    valueType: 'formList',
    fieldProps: {
      copyIconProps: false,
      max: 5,
      creatorButtonProps: props.isFormEditable && {
        position: 'bottom',
        creatorButtonText: props.formatMessage({
          id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.addEmploymentIncome.button',
        }),
      },
      itemRender: ({ listDom }: any, { index, operation }: any) => (
        <Card
          title={
            index === 0
              ? props.formatMessage({
                  id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.primaryIncome.label',
                })
              : props.formatMessage({
                  id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.secondaryIncome.label',
                })
          }
          className={'bg-white/60 border-gray-300 border-dashed'}
          extra={
            operation.remove && props.isFormEditable ? (
              <a onClick={() => operation.remove(index)}>
                <DeleteIcon
                  titleAccess={props.formatMessage({
                    id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.removeEmploymentIncome.button',
                  })}
                />
              </a>
            ) : null
          }
          style={{ marginBottom: 16 }}
        >
          {listDom}
        </Card>
      ),
    },
    columns: [
      {
        dataIndex: 'id',
        valueType: 'text',
        formItemProps: { hidden: true, noStyle: true },
        fieldProps: { hidden: true },
      },
      {
        title: props.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.label' }),
        dataIndex: ['employmentType'],
        valueType: 'select',
        fieldProps: {
          showSearch: true,
          options: Object.values(IncomeType).map((type, index) => ({
            label: (
              <FormattedMessage id={`btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.${index}.label`} />
            ),
            value: type,
          })),
        },
        formItemProps: {
          hasFeedback: true,
          rules: [
            {
              message: props.formatMessage({
                id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.error',
              }),
              required: true,
            },
          ],
        },
      },
      {
        valueType: 'dependency',
        name: ['employmentType'],
        columns: ({ employmentType }: { employmentType: IncomeType }) => {
          const includePercentageShareholding = props.variant === 'oo';

          switch (employmentType) {
            case 'PAYE_EMPLOYEE':
              return [...payeEmployeeFormSchema()];
            case 'SOLE_TRADER':
              return [...soleTraderFormSchema()];
            case 'CONTRACTOR':
              return [...contractorFormSchema()];
            case 'SALARIED_DIRECTOR':
              return [...companyDirectorFormSchema({ includePercentageShareholding })];
            case 'PARTNERSHIP':
              return [...partnershipFormSchema({ includePercentageShareholding })];
            default:
              return [];
          }
        },
      },
    ],
  },
  {
    valueType: 'group',
    columns: [
      {
        renderFormItem: () => (
          <h4 className={'mb-0'}>
            {props.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.otherIncome.header' })}
          </h4>
        ),
      },
      ...['pension', 'rental', 'maintenanceReceipts'].map(item =>
        createCurrencyFormItem('otherIncome', item, 'member', 'yearly'),
      ),
    ],
  },
];
